import {
  Chip,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { AbsenceTableCard } from "./Absence.styles";
import { AbsenceEntry, AbsenceApprovalStatus } from "../../../types/absence";
import { GeneralButton } from "../../../common/ui/GeneralButton";
import { PaginatedData } from "../../../hooks/PaginationHooks";
import { ServerSidePagination } from "../../../components/common/table/ServerSidePagination";
import ServerSideTableColumnHeader from "../../../components/common/table/ServerSideTableColumnHeader";
import {useCallback} from "react";

type Props = {
  isLoading: boolean;
  updatingId: number | null;
  onUpdateStatus: (absence: AbsenceEntry, status: AbsenceApprovalStatus) => void;
  paginatedData: PaginatedData<AbsenceEntry>;
};

const statusColor: Record<AbsenceApprovalStatus, "default" | "success" | "error" | "warning"> = {
  Pending: "warning",
  Approved: "success",
  Rejected: "error",
};

const formatDate = (value: string) => dayjs(value).format("DD.MM.YYYY HH:mm");

const approvalButtonSx = { minHeight: 30, padding: "0.35rem 0.75rem", fontSize: "0.8125rem" };

const AbsenceTable = ({
  isLoading,
  updatingId,
  onUpdateStatus,
  paginatedData
}: Props) => {
  const { t } = useTranslation();

  return (
    <AbsenceTableCard>
      <Typography variant="h6">
        {t("serviceModulesPage.absencePage.table.title")}
      </Typography>

      {isLoading ? (
        <CircularProgress />
      ) : paginatedData.items.length === 0 ? (
        <Typography variant="body2" color="textSecondary">
          {t("serviceModulesPage.absencePage.table.empty")}
        </Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                <ServerSideTableColumnHeader
                  label={t("serviceModulesPage.absencePage.table.employee")}
                  fieldName="employeeName"
                  isSorted
                  paginatedData={paginatedData}
                />
              </TableCell>
              <TableCell>
                <ServerSideTableColumnHeader
                  label={t("serviceModulesPage.absencePage.table.start")}
                  fieldName="startTimestamp"
                  isSorted
                  paginatedData={paginatedData}
                />
              </TableCell>
              <TableCell>
                <ServerSideTableColumnHeader
                  label={t("serviceModulesPage.absencePage.table.end")}
                  fieldName="endTimestamp"
                  isSorted
                  paginatedData={paginatedData}
                />
              </TableCell>
              <TableCell>
                {t("serviceModulesPage.absencePage.table.reason")}
              </TableCell>
              <TableCell>
                {t("serviceModulesPage.absencePage.table.status")}
              </TableCell>
              <TableCell align="right">
                {t("serviceModulesPage.absencePage.table.actions")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.items.map((absence) => {
              const isUpdating = updatingId === absence.id;
              return (
                <TableRow key={absence.id}>
                  <TableCell>{absence.employeeName}</TableCell>
                  <TableCell>{formatDate(absence.startTimestamp)}</TableCell>
                  <TableCell>{formatDate(absence.endTimestamp)}</TableCell>
                  <TableCell>
                    {absence.absenceReason
                      ? t(`serviceModulesPage.absencePage.reason.${absence.absenceReason.toLowerCase()}`)
                      : "-"}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={t(`serviceModulesPage.absencePage.status.${absence.absenceApproval.toLowerCase()}`)}
                      color={statusColor[absence.absenceApproval]}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <GeneralButton
                        appVariant="danger"
                        disabled={isUpdating || absence.absenceApproval === "Rejected"}
                        onClick={() => onUpdateStatus(absence, "Rejected")}
                        sx={approvalButtonSx}
                      >
                        {t("serviceModulesPage.absencePage.actions.reject")}
                      </GeneralButton>

                      <GeneralButton
                        appVariant="primary"
                        disabled={isUpdating || absence.absenceApproval === "Approved"}
                        onClick={() => onUpdateStatus(absence, "Approved")}
                        sx={approvalButtonSx}
                      >
                        {t("serviceModulesPage.absencePage.actions.approve")}
                      </GeneralButton>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      <ServerSidePagination paginatedData={paginatedData} />
    </AbsenceTableCard>
  );
};

export default AbsenceTable;
